import { useQuery } from "@tanstack/react-query";
import { tenantThemeApi } from "../api/tenantTheme";
import { queryKeys } from "../api/queryKeys";
import type { TenantThemeOverride } from "../theme/tokens";

export interface UseTenantThemeResult {
  overrides: TenantThemeOverride | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Fetches the current tenant's theme overrides from the API.
 *
 * - Only fires when VITE_FEATURE_TENANT_THEMING=true
 * - Gracefully degrades to null on 404 or network error (shows default theme)
 * - staleTime: Infinity — tenant branding rarely changes mid-session
 * - Logs console.warn (not error) on failure
 */
export function useTenantTheme(): UseTenantThemeResult {
  // Read at call time so vi.stubEnv works in tests
  const enabled = import.meta.env.VITE_FEATURE_TENANT_THEMING === "true";

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.tenantTheme.config(),
    queryFn: async () => {
      try {
        return await tenantThemeApi.getConfig();
      } catch (err) {
        console.warn(
          "[useTenantTheme] Failed to fetch tenant theme config — using default theme.",
          err,
        );
        return null;
      }
    },
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false, // Don't retry — fall back to default theme immediately
  });

  return {
    overrides: data?.overrides ?? null,
    isLoading: enabled ? isLoading : false,
    error: error as Error | null,
  };
}
